import { Injectable } from '@angular/core';
import { finalize, switchMap, take } from 'rxjs/operators';

import { ProtectionPlanRequest } from '@core/models/search/search-request.model';
import { BaseStore } from '@core/store/_base/base.store';
import { GridOrderByModel } from '@shared/modules/grid/models/grid/grid-order-by.model';

import { StoreState } from './my-plans.state';
import IStoreState = StoreState.IStoreState;
import initialState = StoreState.initialState;
import { MyPlanApiService } from '../../api/my-plan-api.service';

@Injectable({
  providedIn: 'root',
})
export class MyPlansStore extends BaseStore<IStoreState> {
  constructor(private readonly myPlanApiService: MyPlanApiService) {
    super(initialState);
  }

  loadData(lazy = false): void {
    this.updateState({ loading: true });
    this.select$('searchRequest')
      .pipe(
        take(1),
        switchMap(searchRequest => this.myPlanApiService.all(searchRequest)),
        finalize(() => this.updateState({ loading: false })),
      )
      .subscribe(({ items, totalCount, page, pageCount }) => {
        const plans = this.get('plans');
        items = lazy ? [...plans, ...items] : items;
        this.updateState({
          plans: items || [],
          pagination: {
            totalCount,
            page,
            pageCount,
          },
        });
      });
  }

  updatePagination(page: number, pageSize?: number, lazy = false) {
    const searchRequest = this.get('searchRequest');
    this.updateState({
      searchRequest: {
        ...searchRequest,
        page,
        pageSize: pageSize || searchRequest.pageSize,
      },
    });
    this.loadData(lazy);
  }

  updatePageSize(pageSize: number) {
    const searchRequest = this.get('searchRequest');
    this.updateState({
      searchRequest: {
        ...searchRequest,
        pageSize,
      },
    });
    this.loadData();
  }

  updateSearches(searchString: string) {
    const searchRequest = this.get('searchRequest');
    this.updateState({
      searchRequest: {
        ...searchRequest,
        page: 1,
        searches: [searchString],
      },
    });
    this.loadData();
  }

  updateOrderBy(orderBy: GridOrderByModel[]) {
    const storeSearchRequest = this.get('searchRequest');
    this.updateState({
      searchRequest: {
        ...storeSearchRequest,
        orderBy,
      },
    });
    this.loadData();
  }

  updateSearchRequest(searchRequest: Partial<ProtectionPlanRequest>) {
    const storeSearchRequest = this.get('searchRequest');
    this.updateState({
      searchRequest: {
        ...storeSearchRequest,
        ...searchRequest,
        page: 1,
      },
    });
  }

  checkAll(isChecked: boolean) {
    const plans = this.get('plans');
    plans.forEach(item => (item.isChecked = isChecked));
    this.updateState({
      plans: [...plans],
    });
  }

  checkItem(checkedItem: any) {
    const plans = this.get('plans');
    plans.forEach(item => {
      if (checkedItem.id === item.id) {
        item.isChecked = checkedItem.isChecked;
      }
    });
    this.updateState({
      plans: [...plans],
    });
  }

  clearSearchRequest() {
    this.updateSearchRequest(initialState.searchRequest);
  }
}
